import { DatePipe } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { RequestService } from "@services/request/request.service";
import { formatCountryCode } from "../../utils/country-code.util";

/**
 * Queue of every admin/vendor account currently sitting at status='inactive'
 * — self-service signups (and manual promotions) still waiting on an
 * existing admin to activate them. Activating from here calls the same
 * PATCH /api/v1/user endpoint the user-details page's status control uses.
 */
@Component({
  selector: "fh-access-approval",
  templateUrl: "./access-approval.component.html",
  styleUrls: ["./access-approval.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    FilterBarComponent,
    DataTableComponent,
    RefreshButtonComponent,
  ],
  providers: [DatePipe],
})
export class AccessApprovalComponent implements OnInit {
  refreshData = (): void => this.getPendingAccounts();

  searchText = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("updated_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);
  activatingId = signal<number | null>(null);

  accounts = signal<any[]>([]);
  total = signal<number>(0);

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.accounts(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
  }));

  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "check_circle",
      label: "Activate",
      handler: (row, event) => this.activate(row, event),
      color: "primary",
      disabled: (row) => this.activatingId() === row.id,
    },
  ]);

  readonly paginatorConfig = paginatorConfig;

  constructor(
    private requestService: RequestService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.searchText.set(params.search || "");
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.pageIndex.set(+(params.page || 0));
      this.sortProp.set(params.sortProp || "updated_on");
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");
      this.getPendingAccounts();
    });
  }

  onSearchChange(searchText: string): void {
    this.updateQueryParams({ search: searchText || undefined, page: 0 });
  }

  onResetFilters(): void {
    this.updateQueryParams({ search: undefined, page: 0 });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({ page: event.pageIndex, pageSize: event.pageSize });
  }

  activate(row: any, event?: Event): void {
    event?.stopPropagation();
    if (!confirm(`Activate ${row.full_name?.trim() || row.email}'s ${row.access} account?`)) {
      return;
    }

    this.activatingId.set(row.id);
    this.requestService.patch("/api/v1/user", { userId: row.id, status: "active" }).subscribe({
      next: (res: any) => {
        this.activatingId.set(null);
        if (res.result_code === 1) {
          this.showMessage(`${row.email} activated — a notification email was sent.`);
          this.getPendingAccounts();
        } else {
          this.showMessage(res.message || "Failed to activate account");
        }
      },
      error: (err) => {
        this.activatingId.set(null);
        console.error("Failed to activate account:", err);
        this.showMessage("Failed to activate account");
      },
    });
  }

  private getPendingAccounts(): void {
    const params = new URLSearchParams({
      pageIndex: String(this.pageIndex()),
      pageSize: String(this.pageSize()),
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
    });
    if (this.searchText()) params.set("search", this.searchText());

    this.isLoading.set(true);
    this.requestService.get(`/api/v2/access-approvals?${params.toString()}`).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.accounts.set(res.data?.list || []);
          this.total.set(res.data?.count || 0);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error("Error fetching access approvals:", error);
        this.accounts.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, "Close", {
      duration: 4000,
      horizontalPosition: "end",
      verticalPosition: "top",
    });
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      { key: "id", header: "ID", sortable: true, width: "80px" },
      { key: "full_name", header: "Name", sortable: true, width: "200px" },
      { key: "email", header: "Email", sortable: true, width: "220px" },
      {
        key: "access",
        header: "Requested Access",
        sortable: true,
        width: "140px",
        format: (value) => (value === "admin" ? "Admin" : "Vendor"),
        cssClass: (row) => `access-${row.access}`,
      },
      {
        key: "phone",
        header: "Phone",
        sortable: false,
        width: "140px",
        format: (_value, row) =>
          row.phone_number ? `${row.phone_country_code || ""}${row.phone_number}` : "—",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: false,
        width: "100px",
        format: formatCountryCode,
      },
      {
        // updated_on, not created_on: an account created years ago (e.g. an
        // existing customer) that just now attempted to become a vendor/admin
        // has updated_on bumped to the moment of that attempt, while
        // created_on stays at the account's original signup date — this is
        // the column that actually reflects "when did they ask for this".
        key: "updated_on",
        header: "Requested On",
        sortable: true,
        type: "date",
        width: "160px",
        format: (value) => {
          const t = this.datePipe.transform(value, "dd MMM yy, h:mm a", "+0800");
          return t ? `${t} SGT` : "—";
        },
      },
      {
        key: "created_on",
        header: "Account Created",
        sortable: true,
        type: "date",
        width: "160px",
        format: (value) => {
          const t = this.datePipe.transform(value, "dd MMM yy, h:mm a", "+0800");
          return t ? `${t} SGT` : "—";
        },
      },
    ];
  }
}
